import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import _isEmpty from 'lodash/isEmpty';
import _get from 'lodash/get';
import { FormattedMessage } from 'react-intl';

// Components
import Paper from '../Paper';
import Heading from '../Heading';
import CartItemCompact from '../CartItem/CartItemCompact';
import Amount from '../Amount';
import Button from '../Button';
import AppLink from '../AppLink';
import Preloader from '../Preloader';
import ToggleArrow from '../ToggleArrow';
import Collapse from '../Collapse';
import Form from '../Form';

// Router
import { getRoute } from '../../entry/routes';

// Actions
import * as cartActions from '../../state/modules/cart/actions';
import * as orderActions from '../../state/modules/order/actions';
import * as sailplayActions from '../../state/modules/sailplay/actions';
import * as userActions from '../../state/modules/user/actions';
import * as stockActions from '../../state/modules/stock/actions';

// Selectors
import { unusedBonusesSum } from '../../state/modules/user/selectors';
import { totalCountItemsSelector } from '../../state/modules/cart/selectors';
import { priceIncludingBonusesSelector } from '../../state/modules/order/selectors';

import { CITY_THEME_DISNEY, isThemeVisualEnabled } from '../../utils/cityTheme';
import CartIcon from '../../icons/cart.svg';
import missionControlImg from '../../styles/theme/disney/Mission_Control.jpg';
import grabberClawImg from '../../styles/theme/disney/GrabberClaw_small.png';

import ApplePay from './partials/ApplePay';

import Empty from './Empty';

import styles from './Cart.styl';
import UpsaleSlider from '../Checkout/UpsaleSlider/ViewForCart';

const cx = classNames.bind(styles);

class Cart extends PureComponent {
  static defaultProps = {
    className: '',
    error: '',
    priceIncludingBonuses: 0,
  };

  static propTypes = {
    className: PropTypes.string,
    isPhone: PropTypes.bool,
    composition: PropTypes.array.isRequired,
    isLoading: PropTypes.bool.isRequired,
    isLoaded: PropTypes.bool.isRequired,
    updateItem: PropTypes.func.isRequired,
    removeItem: PropTypes.func.isRequired,
    fetchCartItems: PropTypes.func.isRequired,
    clearCart: PropTypes.func.isRequired,
    resetCheckoutPayment: PropTypes.func.isRequired,
    fetchOrderList: PropTypes.func.isRequired,
    setBonuses: PropTypes.func.isRequired,
    clearStock: PropTypes.func.isRequired,
    price: PropTypes.number,
    cash_back: PropTypes.number.isRequired,
    totalCount: PropTypes.number.isRequired,
    isAuthenticated: PropTypes.bool.isRequired,
    isOpen: PropTypes.bool.isRequired,
    toggle: PropTypes.func.isRequired,
    error: PropTypes.string,
    priceIncludingBonuses: PropTypes.number,
    cancelBonusesUsage: PropTypes.func.isRequired,
    usedBonuses: PropTypes.number,
    applePayExtended: PropTypes.number,
    applePay: PropTypes.bool,
    isPayed: PropTypes.bool,
    isApplying: PropTypes.bool,
    stockIsApplied: PropTypes.bool,
    unusedBonusesSum: PropTypes.number,
    wantedTimeError: PropTypes.string,
    deliveryType: PropTypes.string,
    deliveryMinSum: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    userCityTheme: PropTypes.string,
  };

  componentWillReceiveProps({ isAuthenticated, isPayed, stockIsApplied }) {
    // это всё для ApplePay, тк его можно провести через корзину
    if (isPayed && isPayed !== this.props.isPayed) {
      if (!isAuthenticated) {
        this.props.fetchCartItems();
      } else {
        if (this.props.usedBonuses) {
          this.props.cancelBonusesUsage();
        }

        // Устанавливаем новое кол-во бонусов
        this.props.fetchCartItems().then(() => {
          this.props.setBonuses(this.props.unusedBonusesSum);
        });
      }

      // Если был активирован промокод, то отменяем его
      if (stockIsApplied) {
        this.props.clearStock();
      }

      this.props.resetCheckoutPayment();
      this.props.fetchOrderList();
    }
  }

  clear = e => {
    e.preventDefault();
    this.props.clearCart();

    if (this.props.priceIncludingBonuses) {
      this.props.cancelBonusesUsage();
    }
  };

  renderItems = () => (
    <div className={cx('Cart__list')}>
      {this.props.composition.map(good => (
        <CartItemCompact key={good.hash} good={good} />
      ))}
    </div>
  );

  render() {
    const {
      composition,
      totalCount,
      isLoaded,
      isOpen,
      price,
      priceIncludingBonuses,
      cash_back,
      applePayExtended,
      applePay,
      wantedTimeError,
      isApplying
    } = this.props;
    const hasItems = totalCount > 0;
    const isDisneyTheme =
      this.props.userCityTheme === CITY_THEME_DISNEY && isThemeVisualEnabled(CITY_THEME_DISNEY);
    return (
      <div
        className={cx('Cart', this.props.className, {
          Cart_opened: isOpen,
          Cart_discount: priceIncludingBonuses && priceIncludingBonuses !== price,
          Cart_disney: isDisneyTheme,
        })}
      >
        <div className={cx('Cart__line')} />
        <div className={cx('Cart__decor')} />
        <Paper className={cx('Cart__inner')}>
          <div className={cx('Cart__icon')}>
            <CartIcon width={32} height={32} />
          </div>
          <div className={cx('Cart__header')}>
            <div className={cx('Cart__headline')} onClick={this.props.toggle}>
              <div className={cx('Cart__title')}>
                <Heading className={cx('Cart__heading')} level={3} tagName="div">
                  <FormattedMessage id="cart.title" />
                </Heading>{' '}
                {hasItems && isOpen && <div className={cx('Cart__count')}>({totalCount})</div>}
              </div>
              <ToggleArrow className={cx('Cart__toggle')} isOpen={isOpen} />
            </div>
            {hasItems && (
              <AppLink href="#" className={cx('Cart__clear')} onClick={this.clear}>
                <FormattedMessage id="cart.clear" />
              </AppLink>
            )}
          </div>
          <Collapse isOpen={this.props.isOpen}>
            <div className={cx('Cart__content')}>
              {this.props.isLoading && <Preloader />}
              {((isLoaded && _isEmpty(composition)) || this.props.error) && <Empty />}
              {isLoaded && !_isEmpty(composition) && this.renderItems()}
            </div>
          </Collapse>
          {hasItems && (
            <React.Fragment>
              <div className={cx('Cart__total')}>
                {!isOpen ? (
                  <div className={cx('Cart__total-count')}>
                    <FormattedMessage id="cart.count" values={{ totalCount }} />
                  </div>
                ) : (
                  <Heading level={4} tagName="div" className={cx('Cart__total-label')}>
                    <FormattedMessage id="cart.totalPrice" />
                  </Heading>
                )}
                <Amount
                  className={cx('Cart__total-value')}
                  value={price}
                  newValue={priceIncludingBonuses}
                />
              </div>
              {Boolean(applePayExtended) && applePay && (
                <div className={cx('Cart__pay')}>
                  <ApplePay
                    price={priceIncludingBonuses}
                    discount={priceIncludingBonuses - price}
                  />
                </div>
              )}
              <UpsaleSlider />
              <div className={cx('Cart__actions')}>
                <Button disabled={isApplying} to={getRoute('order')}>
                  <FormattedMessage id="button.checkout" />
                </Button>
              </div>
              {cash_back > 0 && (
                <div className={cx('Cart__cashback')}>
                  <FormattedMessage id="cart.cashback" values={{ cashback: cash_back }} />
                </div>
              )}
              <Form.Error error={wantedTimeError} />
            </React.Fragment>
          )}
          {isDisneyTheme && (
            <img className={cx('Cart__mission')} src={missionControlImg} alt="" role="presentation" />
          )}
        </Paper>
        {isDisneyTheme && (
          <img className={cx('Cart__grabber')} src={grabberClawImg} alt="" role="presentation" />
        )}
      </div>
    );
  }
}

const connector = connect(
  state => ({
    ...state.cart,
    applePayExtended: _get(state, 'city.userCity.apple_pay_extended', 0),
    applePay: state.payTypes.applePay,
    isAuthenticated: state.user.isAuthenticated,
    totalCount: totalCountItemsSelector(state),
    priceIncludingBonuses: priceIncludingBonusesSelector(state),
    deliveryType: state.order.checkout.deliveryType || '',
    deliveryMinSum: _get(state, 'order.checkout.currentRestaurant.min_sum', ''),
    stockIsApplied: state.stock.isApplied,
    usedBonuses: state.sailplay.usedBonuses,
    isPhone: state.responsive.isPhone || false,
    isPayed: state.order.checkout.isPayed,
    wantedTimeError: _get(state, 'order.checkout.error.wanted_time', ''),
    unusedBonusesSum: unusedBonusesSum(state),
    isApplying: _get(state, 'stock.isApplying', false),
    userCityTheme: _get(state, 'city.userCity.theme', ''),
  }),
  dispatch =>
    bindActionCreators(
      {
        ...cartActions,
        ...orderActions,
        ...sailplayActions,
        ...userActions,
        ...stockActions,
      },
      dispatch,
    ),
);

export default connector(Cart);
